<template>
    <section class="hero" :class="`${$route.params.heroVersion}`" :style="[{'--gridRow': rowSize ? `${rowSize}px` : null}, {'--grid-first-item-row-end': (firstItemRowEnd)}]">

        <GridSectionBackground :hide-fade-in="true" @set-row-size="setRowSize">

            <GridSectionContentBox class="tagline item-1" :row-size="rowSize" @set-row-span="value => firstItemRowEnd = value">
                <h1 class="h1">
                    Build the UX portfolio that<br> gets you <span class="hero-highlight"><span class="hero-highlight-text">hired</span><img :src="markerHighlight" alt="" aria-hidden="true" role="presentation" /></span>
                </h1>
            </GridSectionContentBox>

            <div class="cta">
                <CtaButton label="Get started for free" @click="goToSignup()" />
            </div>

        </GridSectionBackground>

        <div class="hero-portfolio-cards">
            <picture
                class="portfolio-card side left"
                :class="getPortfolioCardPositionClass('left')"
                @click="handlePortfolioCardClick('left')"
            >
                <source :srcset="leftCardImageWebp" type="image/webp" />
                <img :src="leftCardImage" alt="Light mode UX portfolio preview with case study cards" />
            </picture>
            <picture
                class="portfolio-card center"
                :class="getPortfolioCardPositionClass('center')"
                @click="handlePortfolioCardClick('center')"
            >
                <source :srcset="centerCardImageWebp" type="image/webp" />
                <img :src="centerCardImage" alt="Minimal UX portfolio preview with case study cards" />
            </picture>
            <picture
                class="portfolio-card side right"
                :class="getPortfolioCardPositionClass('right')"
                @click="handlePortfolioCardClick('right')"
            >
                <source :srcset="rightCardImageWebp" type="image/webp" />
                <img :src="rightCardImage" alt="Dark mode UX portfolio preview with case study cards" />
            </picture>
        </div>

    </section>
</template>

<script>
    import CtaButton from "./CtaButton.vue";
    import GridSectionBackground from "./GridSectionBackground.vue";
    import GridSectionContentBox from "./GridSectionContentBox.vue";
    import {getLandingPageName, trackLandingCtaClick} from "@/scripts/view/publicPages/utilities/landingCtaTracking.js";
    import {registerTo} from "@/scripts/auth/keycloakAuth.js";
    import leftCardImage from "@/images/landing-media/hero-portfolio-1.png";
    import leftCardImageWebp from "@/images/landing-media/hero-portfolio-1.webp";
    import centerCardImage from "@/images/landing-media/hero-portfolio-2.png";
    import centerCardImageWebp from "@/images/landing-media/hero-portfolio-2.webp";
    import rightCardImage from "@/images/landing-media/hero-portfolio-3.png";
    import rightCardImageWebp from "@/images/landing-media/hero-portfolio-3.webp";
    import markerHighlight from "@/images/hero-cards/marker-highlight.svg";

    export default {
        name: "Hero",
        components: {
            GridSectionBackground,
            GridSectionContentBox,
            CtaButton
        },
        data() {
            return {
                rowSize: null,
                firstItemRowEnd: 3,
                leftCardImage,
                leftCardImageWebp,
                centerCardImage,
                centerCardImageWebp,
                rightCardImage,
                rightCardImageWebp,
                markerHighlight,
                portfolioCardOrder: ["left", "center", "right"],
                portfolioCardTimer: null
            }
        },
        mounted() {
            this.startPortfolioCardRotation();
            window.addEventListener("scroll", this.handleHeroScroll, { passive: true });
            this.handleHeroScroll();
        },
        beforeUnmount() {
            this.stopPortfolioCardRotation();
            window.removeEventListener("scroll", this.handleHeroScroll);
        },
        methods: {
            setRowSize(value) {
                this.rowSize = value
            },
            getPortfolioCardPositionClass(cardKey) {
                const position = this.portfolioCardOrder.indexOf(cardKey);

                if (position === 0) {
                    return "v5-position-left";
                }

                if (position === 1) {
                    return "v5-position-center";
                }

                return "v5-position-right";
            },
            startPortfolioCardRotation(restart = false) {
                if (this.portfolioCardTimer && !restart) {
                    return;
                }

                clearInterval(this.portfolioCardTimer);
                this.portfolioCardTimer = setInterval(() => this.advancePortfolioCards(), 2500);
            },
            stopPortfolioCardRotation() {
                clearInterval(this.portfolioCardTimer);
                this.portfolioCardTimer = null;
            },
            advancePortfolioCards() {
                const [leftCard, centerCard, rightCard] = this.portfolioCardOrder;
                this.portfolioCardOrder = [rightCard, leftCard, centerCard];
            },
            handleHeroScroll() {
                if (window.scrollY > 8) {
                    this.stopPortfolioCardRotation();
                    return;
                }

                this.startPortfolioCardRotation();
            },
            handlePortfolioCardClick(cardKey) {
                if (this.getPortfolioCardPositionClass(cardKey) === "v5-position-center") {
                    return;
                }

                this.centerPortfolioCard(cardKey);
            },
            centerPortfolioCard(cardKey) {
                const currentIndex = this.portfolioCardOrder.indexOf(cardKey);
                const previousCard = this.portfolioCardOrder[(currentIndex + this.portfolioCardOrder.length - 1) % this.portfolioCardOrder.length];
                const nextCard = this.portfolioCardOrder[(currentIndex + 1) % this.portfolioCardOrder.length];

                this.portfolioCardOrder = [previousCard, cardKey, nextCard];
                this.startPortfolioCardRotation(true);
            },
            goToSignup() {
                const utmCampaignSource = this.$route.fullPath === "/" ? "/landing" : this.$route.fullPath;

                trackLandingCtaClick("Sign up", getLandingPageName(this.$route.path), "Hero section", utmCampaignSource, {
                    destination: "/home/onboarding",
                });

                return registerTo('/home/onboarding', {
                    utm_source: utmCampaignSource,
                    utm_campaign: 'Hero CTA',
                });
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../../../styles/public-pages/components/hero";
</style>
